import { Target, Award, Heart, Users, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const About = () => {
  const values = [
    {
      icon: <Target className="text-[#c9a961]" size={40} />,
      title: 'Précision',
      description: 'Chaque geste compte. Nous travaillons avec une précision chirurgicale pour obtenir une finition parfaite sans hologrammes.',
    },
    {
      icon: <Award className="text-[#c9a961]" size={40} />,
      title: 'Excellence',
      description: 'Nous n’utilisons que des produits haut de gamme et des techniques professionnelles pour garantir des résultats exceptionnels.',
    },
    {
      icon: <Heart className="text-[#c9a961]" size={40} />,
      title: 'Passion',
      description: 'Notre amour pour l’automobile se reflète dans chaque traitement. Votre voiture est notre priorité.',
    },
    {
      icon: <Users className="text-[#c9a961]" size={40} />,
      title: 'Service client',
      description: 'Une écoute attentive et un accompagnement personnalisé pour répondre à tous vos besoins.',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_d9fd72fd-f35d-49e6-8f9e-e0391db52cc1/artifacts/v9rwhrtn_image.png)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/50" />
        <div className="relative z-10 text-center text-white px-4">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="text-[#c9a961]" size={24} />
            <span className="text-[#c9a961] font-medium tracking-wider uppercase">Notre Histoire</span>
          </div>
          <h1 className="hero-title text-5xl md:text-7xl mb-4">À propos de SUKI</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Passion, excellence et dévouement pour l'esthétique automobile
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="section-title text-4xl md:text-5xl text-[#1a1a1a] gold-line pb-4">
                Notre Philosophie
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Chez SUKI, nous croyons que chaque véhicule mérite un traitement exceptionnel. Notre approche se distingue par notre refus du travail bâclé et notre engagement envers la perfection.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Nous nous concentrons sur les corrections approfondies de peinture et les polissages professionnels, où chaque détail compte. Nous ne proposons pas de traitements standard sans effet visible.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Nous travaillons avec précision et méthode, afin que chaque voiture obtienne un éclat sublime sans hologrammes, une correction maximale de la peinture et une finition haut de gamme qui dure dans le temps.
              </p>
              <div className="pt-4">
                <blockquote className="border-l-4 border-[#c9a961] pl-6 italic text-gray-600 text-lg">
                  "Nous ne faisons pas que nettoyer des voitures, nous révélons leur véritable beauté."
                </blockquote>
              </div>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_d9fd72fd-f35d-49e6-8f9e-e0391db52cc1/artifacts/qff2sz0t_image.png"
                alt="SUKI philosophy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why SUKI Section */}
      <section className="py-20 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl mb-4 text-[#1a1a1a] gold-line inline-block pb-4">
              Pourquoi choisir SUKI ?
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto mt-6">
              Ce qui nous distingue des autres centres de detailing à Montmagny
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card
                key={index}
                className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="mb-4">{value.icon}</div>
                  <h3 className="text-2xl font-semibold mb-3 section-title">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title text-4xl md:text-5xl mb-4 text-[#1a1a1a] gold-line inline-block pb-4">
              Notre Méthode de Travail
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto mt-6">
              Un processus rigoureux pour des résultats irréprochables
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-[#c9a961]/10 flex items-center justify-center mx-auto">
                <span className="text-3xl font-bold text-[#c9a961] section-title">1</span>
              </div>
              <h3 className="text-xl font-semibold section-title">Diagnostic</h3>
              <p className="text-gray-600">
                Inspection minutieuse de l'état de la peinture et identification des défauts à corriger
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-[#c9a961]/10 flex items-center justify-center mx-auto">
                <span className="text-3xl font-bold text-[#c9a961] section-title">2</span>
              </div>
              <h3 className="text-xl font-semibold section-title">Préparation</h3>
              <p className="text-gray-600">
                Lavage détaillé, décontamination complète et préparation de la surface
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-[#c9a961]/10 flex items-center justify-center mx-auto">
                <span className="text-3xl font-bold text-[#c9a961] section-title">3</span>
              </div>
              <h3 className="text-xl font-semibold section-title">Polissage</h3>
              <p className="text-gray-600">
                Correction de peinture multi-étapes avec technique professionnelle sans hologrammes
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-[#c9a961]/10 flex items-center justify-center mx-auto">
                <span className="text-3xl font-bold text-[#c9a961] section-title">4</span>
              </div>
              <h3 className="text-xl font-semibold section-title">Protection</h3>
              <p className="text-gray-600">
                Application de protection haut de gamme (cire, nano ou céramique) selon le forfait
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-[#c9a961]/10 flex items-center justify-center mx-auto">
                <span className="text-3xl font-bold text-[#c9a961] section-title">5</span>
              </div>
              <h3 className="text-xl font-semibold section-title">Finition</h3>
              <p className="text-gray-600">
                Inspection finale, nettoyage des détails et vérification de la perfection
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-[#c9a961]/10 flex items-center justify-center mx-auto">
                <span className="text-3xl font-bold text-[#c9a961] section-title">6</span>
              </div>
              <h3 className="text-xl font-semibold section-title">Livraison</h3>
              <p className="text-gray-600">
                Présentation du résultat et conseils d'entretien personnalisés
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-[#1a1a1a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="section-title text-4xl md:text-5xl gold-line pb-4">Notre Équipe</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                Notre équipe est composée de professionnels passionnés, formés aux techniques les plus avancées du detailing automobile.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Chaque membre apporte son expertise unique pour garantir que votre véhicule reçoive le meilleur traitement possible.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Nous investissons continuellement dans la formation et l'équipement professionnel pour rester à la pointe de l'industrie.
              </p>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_d9fd72fd-f35d-49e6-8f9e-e0391db52cc1/artifacts/o83ziipm_image.png"
                alt="SUKI team at work"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#f8f7f5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="hero-title text-4xl md:text-5xl mb-6 text-[#1a1a1a]">Rejoignez nos clients satisfaits</h2>
          <p className="text-xl text-gray-600 mb-8">
            Découvrez pourquoi SUKI est la référence du detailing automobile à Montmagny
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/reservation"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#c9a961] text-white rounded-full text-lg font-semibold hover:bg-[#b8984e] transition-all duration-300"
              data-testid="about-cta-book"
            >
              Réserver maintenant
            </a>
            <a
              href="/services"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#1a1a1a] text-white rounded-full text-lg font-semibold hover:bg-[#2a2a2a] transition-all duration-300"
              data-testid="about-cta-services"
            >
              Voir nos services
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
